import { Alert, Button, Card, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { errorMessage } from '../api/client';
import { requisitesApi } from '../api/studio';
import { ru } from '../i18n/ru';
import { requisitesSchema, type RequisitesValues } from './schemas';

/**
 * Реквизиты студии для квитанции об оплате.
 *
 * Живут в профиле администратора, но хранятся одной записью на всю студию,
 * а не у пользователя: квитанция не должна меняться от того, кто из
 * администраторов вошёл в систему.
 */
export function RequisitesCard() {
  const queryClient = useQueryClient();
  const requisites = useQuery({ queryKey: ['requisites'], queryFn: requisitesApi.get });

  const form = useForm<RequisitesValues>({
    resolver: zodResolver(requisitesSchema),
    defaultValues: {
      businessName: '',
      entrepreneurFullName: '',
      inn: '',
      taxSystem: '',
      sellerPosition: '',
      sellerFullName: '',
    },
  });

  const { reset } = form;

  // Форма заполняется после загрузки: значения приходят с сервера позже,
  // чем useForm получает defaultValues.
  useEffect(() => {
    const saved = requisites.data;

    if (saved !== undefined) {
      reset({
        businessName: saved.businessName,
        entrepreneurFullName: saved.entrepreneurFullName,
        inn: saved.inn,
        taxSystem: saved.taxSystem,
        sellerPosition: saved.sellerPosition,
        sellerFullName: saved.sellerFullName,
      });
    }
  }, [requisites.data, reset]);

  const save = useMutation({
    mutationFn: (values: RequisitesValues) => requisitesApi.save(values),
    onSuccess: async (saved) => {
      notifications.show({ color: 'green', message: ru.requisites.saved });
      queryClient.setQueryData(['requisites'], saved);
      await queryClient.invalidateQueries({ queryKey: ['requisites'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: ru.common.error, message: errorMessage(error) });
    },
  });

  const onSubmit = form.handleSubmit((values) => save.mutate(values));

  return (
    <Card padding="lg">
      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={3}>{ru.requisites.title}</Title>
            <Text size="sm" c="graphite.6">
              {ru.requisites.hint}
            </Text>
          </Stack>

          {requisites.isPending && <Loader size="sm" />}

          {requisites.isError && (
            <Alert color="red" title={ru.common.error}>
              {errorMessage(requisites.error, ru.common.loadFailed)}
            </Alert>
          )}

          {requisites.isSuccess && !requisites.data.isComplete && (
            <Alert color="yellow" variant="light">
              {ru.requisites.incomplete}
            </Alert>
          )}

          <TextInput
            label={ru.requisites.businessName}
            placeholder={ru.requisites.businessNamePlaceholder}
            error={form.formState.errors.businessName?.message}
            {...form.register('businessName')}
          />
          <TextInput
            label={ru.requisites.entrepreneurFullName}
            error={form.formState.errors.entrepreneurFullName?.message}
            {...form.register('entrepreneurFullName')}
          />
          <TextInput
            label={ru.requisites.inn}
            inputMode="numeric"
            error={form.formState.errors.inn?.message}
            {...form.register('inn')}
          />
          <TextInput
            label={ru.requisites.taxSystem}
            error={form.formState.errors.taxSystem?.message}
            {...form.register('taxSystem')}
          />
          <TextInput
            label={ru.requisites.sellerPosition}
            error={form.formState.errors.sellerPosition?.message}
            {...form.register('sellerPosition')}
          />
          <TextInput
            label={ru.requisites.sellerFullName}
            error={form.formState.errors.sellerFullName?.message}
            {...form.register('sellerFullName')}
          />

          <Button type="submit" loading={save.isPending} w="fit-content">
            {ru.profile.save}
          </Button>
        </Stack>
      </form>
    </Card>
  );
}
